'use client';

import {useParams, useRouter} from 'next/navigation';
import {useTranslation} from 'react-i18next';
import {trackProjectsListingCardClick, trackProjectsListingRegisterInterestClick, trackProjectsListingViewItemClick} from '../analytics';
import {ListingPage, LOCATION_ICON, type ListingCardProps} from '../components/listing';
import {OrbitMediaBand, type OrbitMediaBandItem} from '../components/orbit-media';
import {openRegisterInterest} from '../components/register-interest/register-interest-controller';
import {PROJECTS_EMPTY} from '../content';
import {usePagination} from '../hooks';
import {type ProjectCardView} from '../model';
import {projectHref} from '../routing';

export type {OrbitMediaBandItem};

const PAGE_SIZE = 9;

export interface ProjectsListingPageProps {
  projects: ProjectCardView[];
  featured?: OrbitMediaBandItem;
}

export function ProjectsListingPage({projects, featured}: ProjectsListingPageProps) {
  const {t} = useTranslation();
  const router = useRouter();
  const {locale} = useParams<{locale: string}>();
  const {pageItems, paginationProps} = usePagination(projects, PAGE_SIZE);

  const cards: ListingCardProps[] = pageItems.map(project => {
    const tracked = {id: project.uuid, name: project.title};
    return {
      title: project.title,
      image: project.image.src,
      href: projectHref(locale, project.uuid),
      primaryMeta: project.location ? {icon: LOCATION_ICON, label: project.location} : undefined,
      onClick: () => {
        trackProjectsListingCardClick(tracked);
      },
      hoverActions: [
        {
          label: t('register_interest'),
          onClick: () => {
            trackProjectsListingRegisterInterestClick(tracked);
            openRegisterInterest({projectId: project.uuid});
          },
        },
        {
          label: t('view_project'),
          onClick: () => {
            trackProjectsListingViewItemClick(tracked);
            router.push(projectHref(locale, project.uuid));
          },
        },
      ],
    };
  });

  return (
    <ListingPage
      title={t('projects')}
      cards={cards}
      featured={featured ? <OrbitMediaBand item={featured} /> : undefined}
      empty={PROJECTS_EMPTY}
      pagination={paginationProps}
    />
  );
}

ProjectsListingPage.displayName = 'ProjectsListingPage';
